import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  TrendingUp, Award, Star, Search, RefreshCw, Check, X
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '../../components/LoadingSpinner';

function ProductCard({ product, onToggle, type }) {
  const isHighlighted = type === 'trending' ? product.is_trending : 
                        type === 'top_selling' ? product.is_top_selling : 
                        product.is_featured;
  
  return (
    <div className={`flex items-center gap-4 p-4 rounded-lg border ${isHighlighted ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'}`}>
      <img 
        src={product.images?.[0] || 'https://via.placeholder.com/80'} 
        alt={product.name}
        className="w-16 h-16 object-cover rounded-lg"
      />
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-900 truncate">{product.name}</h4>
        <p className="text-sm text-gray-500">₹{product.price}</p>
        <div className="flex gap-1 mt-1">
          {product.is_trending && <Badge variant="outline" className="text-xs bg-orange-50 text-orange-600">Trending</Badge>}
          {product.is_top_selling && <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600">Top Selling</Badge>}
          {product.is_featured && <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600">Featured</Badge>}
        </div>
      </div>
      <Button
        variant={isHighlighted ? "default" : "outline"}
        size="sm"
        onClick={() => onToggle(product, type, !isHighlighted)}
        className={isHighlighted ? "bg-green-600 hover:bg-green-700" : ""}
      >
        {isHighlighted ? <Check className="w-4 h-4 mr-1" /> : <X className="w-4 h-4 mr-1" />}
        {isHighlighted ? 'Active' : 'Add'}
      </Button>
    </div>
  );
}

function HighlightSection({ title, description, type, icon: Icon, color, products, onToggle, loading }) {
  const [search, setSearch] = useState('');
  
  const highlightedProducts = products.filter(p => 
    type === 'trending' ? p.is_trending : 
    type === 'top_selling' ? p.is_top_selling : 
    p.is_featured
  );
  
  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              {title}
              <Badge variant="secondary">{highlightedProducts.length} active</Badge>
            </div>
            <p className="text-sm font-normal text-gray-500">{description}</p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="pl-10"
          />
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product._id}
                product={product}
                onToggle={onToggle}
                type={type}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminProductHighlights() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_URL}/api/admin/products/all`);
      setProducts(response.data || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (product, type, value) => {
    try {
      setUpdating(true);
      const updateData = {
        is_trending: type === 'trending' ? value : product.is_trending || false,
        is_top_selling: type === 'top_selling' ? value : product.is_top_selling || false,
        is_featured: type === 'featured' ? value : product.is_featured || false
      };
      
      await axiosInstance.put(`${API_URL}/api/admin/products/${product._id}/highlight`, updateData);
      
      // Update local state
      setProducts(prev => prev.map(p => 
        p._id === product._id ? { ...p, ...updateData } : p
      ));
      
      toast.success(`Product ${value ? 'added to' : 'removed from'} ${type.replace('_', ' ')}!`);
    } catch (error) {
      toast.error('Failed to update product');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Product Highlights</h1>
          <p className="text-gray-600 mt-1">Manage Trending, Top Selling, and Featured products</p>
        </div>
        <Button variant="outline" onClick={fetchProducts} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="trending">
        <TabsList className="bg-white border p-1 rounded-xl">
          <TabsTrigger value="trending" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
            <TrendingUp className="w-4 h-4 mr-2" />
            Trending
          </TabsTrigger>
          <TabsTrigger value="top_selling" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
            <Award className="w-4 h-4 mr-2" />
            Top Selling
          </TabsTrigger>
          <TabsTrigger value="featured" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
            <Star className="w-4 h-4 mr-2" />
            Featured
          </TabsTrigger>
        </TabsList>

        <TabsContent value="trending">
          <HighlightSection
            title="Trending Products"
            description="Products that appear in the Trending section on homepage"
            type="trending"
            icon={TrendingUp}
            color="bg-orange-500"
            products={products}
            onToggle={handleToggle}
            loading={loading}
          />
        </TabsContent>

        <TabsContent value="top_selling">
          <HighlightSection
            title="Top Selling Products"
            description="Products marked as best sellers"
            type="top_selling"
            icon={Award}
            color="bg-blue-500"
            products={products}
            onToggle={handleToggle}
            loading={loading}
          />
        </TabsContent>

        <TabsContent value="featured">
          <HighlightSection
            title="Featured Products"
            description="Hand-picked products for special promotions"
            type="featured"
            icon={Star}
            color="bg-purple-500"
            products={products}
            onToggle={handleToggle}
            loading={loading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
